import React, { useMemo, useCallback } from 'react';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import styles from '@/common/containers/TasksList/styles.less';
import taskStyles from '@/common/styles/taskStatus.less';
import MuiTable from '@/common/components/Table/index';
import { routes } from '@/app/routing/index';
import { ReactBB } from '@/common/utils/index';
import { TASK_STATUS } from '@/common/constants/constants';
import { getTaskStatusLabel } from '@/common/constants/task-status';
import { useFilters, useLocalizedField } from '@/common/hooks/index';
import { useGeneralMetaDataQuery } from '@/entities/meta-data';
import { useTranslation } from 'react-i18next';

const buildColumns = (t) => [
  t('No.'),
  t('Task'),
  t('Asset category'),
  t('Intended plan'),
  { label: t('ID'), key: 'uid', sortable: true },
  { label: t('Status'), key: 'status', sortable: true },
];

const CATEGORY = (t, key) => {
  const map = {
    real_prop: t('Real estate property'),
    vehicles: t('Vehicles'),
    firearms: t('Firearms'),
    valuables: t('Valuables'),
    fin_liability: t('Financial liability'),
    fin_asset: t('Financial asset'),
    digital_asset: t('Digital asset'),
    livestock: t('Livestock'),
  };
  return map[key] || key;
};

const INTENDED_PLAN_TYPE_BY_CATEGORY = {
  fin_liability: 'DD_LIA_INTENDED_PLAN',
  fin_asset: 'DD_FIN_INTENDED_PLAN',
  real_prop: 'DD_NON_FIN_INTENDED_PLAN',
  vehicles: 'DD_NON_FIN_INTENDED_PLAN',
  firearms: 'DD_NON_FIN_INTENDED_PLAN',
  valuables: 'DD_NON_FIN_INTENDED_PLAN',
  digital_asset: 'DD_NON_FIN_INTENDED_PLAN',
  livestock: 'DD_NON_FIN_INTENDED_PLAN',
};

const sortData = (sortBy, sortOrder, rows = []) => {
  const itemsToSort = [...rows];
  let sortedItems = [];
  let compareFn = null;
  switch (sortBy) {
    case 'uid': {
      compareFn = (list) => {
        let shapedList = list.sort((t1, t2) => {
          return t1.uid &&
            t2.uid &&
            !Number.isNaN(parseInt(t1.uid.match(/\d+/)[0])) &&
            !Number.isNaN(parseInt(t2.uid.match(/\d+/)[0]))
            ? parseInt(t1.uid.match(/\d+/)[0]) -
                parseInt(t2.uid.match(/\d+/)[0])
            : 0;
        });
        if (sortOrder === 'desc') return shapedList.reverse();
        return shapedList;
      };
      break;
    }
    case 'status':
      compareFn = (list) => {
        let shapedList = Object.values(
          list.reduce(
            (acc, item) => {
              acc[item.status].push(item);
              return acc;
            },
            {
              [TASK_STATUS.NEW]: [],
              [TASK_STATUS.ONGOING]: [],
              [TASK_STATUS.ON_HOLD]: [],
              [TASK_STATUS.COMPLETED]: [],
              [TASK_STATUS.SUSPENDED]: [],
              [TASK_STATUS.DEACTIVATED]: [],
            }
          )
        );
        if (sortOrder === 'desc') shapedList = shapedList.reverse();
        shapedList = shapedList.map((arr) => {
          return arr.sort((t1, t2) => {
            return t1.uid &&
              t2.uid &&
              !Number.isNaN(parseInt(t1.uid.match(/\d+/)[0])) &&
              !Number.isNaN(parseInt(t2.uid.match(/\d+/)[0]))
              ? parseInt(t1.uid.match(/\d+/)[0]) -
                  parseInt(t2.uid.match(/\d+/)[0])
              : 0;
          });
        });
        const flatList = shapedList.flat();
        return flatList;
      };
      break;
    default:
      compareFn = undefined;
      break;
  }
  sortedItems = ['status', 'uid'].includes(sortBy)
    ? compareFn(itemsToSort)
    : itemsToSort.sort(compareFn);
  return sortedItems;
};

const AssetTasks = ({ list = [], module }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const getLocalizedField = useLocalizedField();
  const { data: metaData = {} } = useGeneralMetaDataQuery();
  const { dropdowns = {} } = metaData;

  const { sortBy, sortOrder, setSorting, sortedList } = useFilters({
    type: module,
    data: list,
    sortFunc: sortData,
  });

  const requestSort = (pSortBy) => {
    return () => setSorting(pSortBy);
  };

  const getStatusLabel = useCallback(
    (status) => getTaskStatusLabel(t, status),
    [t]
  );

  const getLocalizedAssetDescription = useCallback(
    (task) => {
      const description = getLocalizedField(task, 'description');
      const { typeFieldType, typeValue } = task;

      if (!description || !typeFieldType || !typeValue) {
        return description;
      }

      const options =
        getLocalizedField(dropdowns, typeFieldType) ||
        dropdowns?.[typeFieldType] ||
        [];

      if (!options.length) {
        return description;
      }

      const matchedOption =
        options.find((option) => option.value === typeValue) ||
        options.find((option) => option.label === typeValue);
      const localizedLabel = getLocalizedField(matchedOption, 'label');

      if (!localizedLabel || localizedLabel === typeValue) {
        return description;
      }

      return description.replace(typeValue, localizedLabel);
    },
    [dropdowns, getLocalizedField]
  );

  const getLocalizedIntendedPlan = useCallback(
    (intendedPlan, assetCategory) => {
      if (!intendedPlan) {
        return '-';
      }

      const planType = INTENDED_PLAN_TYPE_BY_CATEGORY[assetCategory];
      if (!planType) {
        return intendedPlan;
      }

      const options =
        getLocalizedField(dropdowns, planType) || dropdowns?.[planType] || [];

      if (!options.length) {
        return intendedPlan;
      }

      const matchedOption =
        options.find((option) => option.value === intendedPlan) ||
        options.find((option) => option.label === intendedPlan);

      return matchedOption?.label || intendedPlan;
    },
    [dropdowns, getLocalizedField]
  );

  const tableData = useMemo(() => {
    return sortedList.map((task, ind) => {
      const { id, intendedPlan, status, assetCategory, uid } = task;
      const description = getLocalizedAssetDescription(task);

      return {
        key: id,
        data: [
          {
            value: ind + 1,
            cellProps: {
              sx: {
                width: '5%',
                fontSize: '18px',
                lineHeight: '24px',
                fontWeight: '300',
                ...(status === 'suspended' && { color: '#9E9E9E' }),
              },
            },
          },
          {
            key: 'description',
            value: <span>{ReactBB(description || '-')}</span>,
            cellProps: {
              sx: {
                width: '28%',
                fontWeight: '500',
                fontSize: '18px',
                lineHeight: '24px',
                ...(status === 'suspended' && { color: '#9E9E9E' }),
              },
            },
          },
          {
            key: 'assetCategory',
            value: CATEGORY(t, assetCategory),
            cellProps: {
              sx: {
                width: '28%',
                fontWeight: '500',
                fontSize: '18px',
                lineHeight: '24px',
                textTransform: 'capitalize',
                ...(status === 'suspended' && { color: '#9E9E9E' }),
              },
            },
          },
          {
            key: 'intendedPlan',
            value: getLocalizedIntendedPlan(intendedPlan, assetCategory),
            cellProps: {
              sx: {
                width: '20%',
                fontWeight: '500',
                fontSize: '18px',
                lineHeight: '24px',
                ...(status === 'suspended' && { color: '#9E9E9E' }),
              },
            },
          },
          {
            key: uid,
            value: uid,
            cellProps: {
              sx: {
                width: '8%',
              },
            },
          },
          {
            key: status,
            value: (
              <span
                className={cx(
                  taskStyles.status,
                  taskStyles[`status-${status}`]
                )}
              >
                {getStatusLabel(status)}
              </span>
            ),
            cellProps: {
              sx: {
                width: '10%',
              },
            },
          },
        ],
        rowProps: {
          className: styles.status,
          onClick: () => history.push(`${routes.dashboard.taskDetails}/${id}`),
          sx: {
            cursor: 'pointer',
          },
        },
      };
    });
  }, [
    getLocalizedAssetDescription,
    getLocalizedIntendedPlan,
    getStatusLabel,
    history,
    sortedList,
    t,
  ]);

  const columns = useMemo(() => buildColumns(t), [t]);

  return (
    <div className={styles.table}>
      <MuiTable
        column={columns}
        data={tableData}
        requestSort={requestSort}
        sortOrder={sortOrder}
        sortBy={sortBy}
      />
    </div>
  );
};

export default AssetTasks;
